import { createClient } from '@supabase/supabase-js';
import { config, validateConfig } from './config';
import type { Product } from './types';
import toast from 'react-hot-toast';

// Nom de la table de base de données
export const PRODUCTS_TABLE = 'lotismode_products';

// Créer un client Supabase avec des options minimales et meilleure gestion des erreurs
export const createSimplifiedClient = () => {
  try {
    // Priorité: utiliser l'API par défaut (REJOO)
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    // Utiliser les paramètres stockés dans localStorage seulement s'ils existent
    const storedUrl = localStorage.getItem('supabase_url');
    const storedKey = localStorage.getItem('supabase_anon_key');
    
    // Déterminer les URL et clés à utiliser (stockées ou par défaut)
    const finalUrl = storedUrl || url;
    const finalKey = storedKey || key;
    
    console.log('Création du client Supabase avec URL:', finalUrl);
    
    // Vérification de l'URL
    if (!finalUrl || !finalUrl.startsWith('http')) {
      console.error('URL Supabase invalide:', finalUrl);
      throw new Error('URL Supabase invalide');
    }
    
    // Création du client avec options supplémentaires
    return createClient(finalUrl, finalKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: true,
      },
      global: {
        fetch: (...args) => {
          return fetch(...args).catch(err => {
            console.error('Erreur fetch Supabase:', err);
            throw new Error('Erreur réseau lors de la connexion à Supabase');
          });
        }
      }
    });
  } catch (error) {
    console.error('Erreur lors de la création du client Supabase:', error);
    throw error;
  }
};

// Client Supabase principal avec meilleure gestion des erreurs
export const supabase = (() => {
  try {
    return createSimplifiedClient();
  } catch (error) {
    console.error('Fallback vers client de secours:', error);
    // Créer un client de secours qui échouera proprement
    return createClient('https://placeholder.supabase.co', 'placeholder-key', {
      auth: {
        persistSession: false,
      }
    });
  }
})();

// Mécanisme de retry amélioré
export const withRetry = async (operation: () => Promise<any>, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      console.warn(`Opération échouée (tentative ${attempt + 1}/${maxRetries}):`, error);
      lastError = error;
      
      if (attempt === maxRetries - 1) {
        break;
      }
      
      // Attendre avant de réessayer avec backoff exponentiel
      const backoffDelay = delay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }
  
  // Si nous arrivons ici, c'est que toutes les tentatives ont échoué
  console.error(`Échec après ${maxRetries} tentatives:`, lastError);
  throw lastError;
};

// Test de connexion amélioré
export const validateConnection = async () => {
  try {
    console.log('Test de connexion à Supabase...');
    
    // Priorité: utiliser l'API par défaut (REJOO)
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    // Utiliser les paramètres stockés dans localStorage seulement s'ils existent
    const storedUrl = localStorage.getItem('supabase_url');
    const storedKey = localStorage.getItem('supabase_anon_key');
    
    // Déterminer l'URL à utiliser (stockée ou par défaut)
    const finalUrl = storedUrl || url;
    
    // Vérification de l'URL
    if (!finalUrl || !finalUrl.startsWith('http')) {
      console.error('URL Supabase invalide:', finalUrl);
      return false;
    }
    
    // Test simple avec timeout plus long
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // Passer à 10 secondes
    
    try {
      // Recréer un client avec les paramètres actuels pour assurer que la connexion est testée avec les dernières valeurs
      const client = createClient(finalUrl, storedKey || key, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        }
      });
      
      const { data, error } = await client
        .from(PRODUCTS_TABLE)
        .select('id')
        .limit(1)
        .abortSignal(controller.signal);
      
      clearTimeout(timeoutId);
      
      if (error) {
        console.error('Erreur de connexion à Supabase:', error);
        return false;
      }
      
      console.log('Connexion à Supabase réussie');
      return true;
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Exception lors du test API Supabase:', error);
      return false;
    }
  } catch (error) {
    console.error('Exception lors du test de connexion à Supabase:', error);
    return false;
  }
};

// Gestionnaire d'erreurs amélioré
export const handleSupabaseError = (error: any): string => {
  if (!error) return 'Erreur inconnue';
  
  // Vérification de la connexion internet
  if (!navigator.onLine) {
    return 'Vous êtes hors ligne. Vérifiez votre connexion internet.';
  }
  
  // Log complet de l'erreur pour débogage
  console.error('Erreur Supabase complète:', {
    message: error.message,
    code: error.code,
    stack: error.stack,
    details: error.details,
    hint: error.hint
  });
  
  // Détection des erreurs réseau
  if (error.name === 'AbortError' || 
      error.message?.includes('abort') || 
      error.message?.includes('network') || 
      error.message?.includes('fetch') ||
      error.message?.includes('NetworkError') ||
      error.code === 'NETWORK_ERROR' ||
      error.code === 'CONNECTION_ERROR') {
    return 'Erreur de connexion au serveur. Vérifiez que l\'URL de Supabase est correcte et accessible.';
  }
  
  // Erreurs d'authentification
  if (error.message?.includes('JWT') || 
      error.message?.includes('token') || 
      error.code === 'INVALID_CREDENTIALS') {
    return 'Problème d\'authentification. Vérifiez votre clé API Supabase.';
  }
  
  // Erreurs spécifiques de Supabase
  if (error.code === '42P01') {
    return 'Table non trouvée. Vérifiez que la table existe dans votre base de données.';
  }
  
  if (error.code === 'PGRST301') {
    return 'Ressource introuvable. Vérifiez l\'URL de votre API Supabase.';
  }
  
  return error.message || 'Erreur de serveur. Veuillez réessayer plus tard.';
};

// S'abonner aux changements en temps réel (version améliorée)
export const subscribeToProducts = (callback: () => void) => {
  try {
    const subscription = supabase
      .channel('schema-db-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: PRODUCTS_TABLE }, () => {
        console.log('Mise à jour reçue, rafraîchissement des données...');
        callback();
      })
      .subscribe((status) => {
        console.log('Statut de l\'abonnement:', status);
        
        if (status === 'TIMED_OUT') {
          console.warn('Abonnement expiré, reconnexion...');
          subscription.unsubscribe();
          subscribeToProducts(callback);
        }
      });
      
    return {
      unsubscribe: () => {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('Erreur lors de la désinscription:', error);
        }
      }
    };
  } catch (error) {
    console.error('Erreur lors de l\'abonnement aux changements:', error);
    return { 
      unsubscribe: () => {} 
    };
  }
};

// Fonction pour obtenir les paramètres de connexion depuis Supabase
export const getConnectionSettings = async () => {
  try {
    // Récupérer les connexions de la table connection_settings
    const { data, error } = await supabase
      .from('connection_settings')
      .select('*')
      .order('updated_at', { ascending: false });
    
    if (error) {
      console.warn('Erreur lors de la récupération des paramètres de connexion:', error);
      return null;
    }
    
    // Récupérer toutes les configurations disponibles
    const connections = data || [];
    
    // Vérifier si une connexion active existe
    // On peut implémenter une logique pour déterminer la connexion active
    // Pour l'instant, on prend simplement la plus récente
    
    if (connections.length === 0) {
      return null;
    }
    
    // Retourner la connexion la plus récemment mise à jour
    return connections[0];
  } catch (error) {
    console.error('Erreur lors de la récupération des paramètres de connexion:', error);
    return null;
  }
};

// Fonction pour mettre à jour les paramètres de connexion dans Supabase
export const updateConnectionSettings = async (settings: {
  id?: string;
  connectionName: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
}) => {
  try {
    const { error } = await supabase
      .from('connection_settings')
      .upsert({
        id: settings.id || '1', // Utiliser l'ID fourni ou '1' par défaut
        connection_name: settings.connectionName,
        supabase_url: settings.supabaseUrl,
        supabase_anon_key: settings.supabaseAnonKey,
        updated_at: new Date().toISOString()
      });
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des paramètres de connexion:', error);
    return false;
  }
};